import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  FileText, Image, ChevronLeft, CheckCircle, XCircle,
  ExternalLink, ZoomIn,
} from 'lucide-react';
import { adminApi } from '@/services/api';

// ─── Schema ──────────────────────────────────────────────────────────────────

const verificationSchema = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('approve'),
    internalNotes: z.string().optional(),
  }),
  z.object({
    action: z.literal('reject'),
    rejectionReason: z.string().min(10, 'Please provide a detailed rejection reason'),
    internalNotes: z.string().optional(),
  }),
]);

type VerificationFormData = z.infer<typeof verificationSchema>;

// ─── Document Viewer ──────────────────────────────────────────────────────────

interface Document {
  id: string;
  type: 'pan_card' | 'citizenship' | 'business_registration' | 'other';
  url: string;
  fileName: string;
  uploadedAt: string;
}

const DOC_LABELS: Record<string, string> = {
  pan_card: 'PAN Card',
  citizenship: 'Citizenship Certificate',
  business_registration: 'Business Registration',
  other: 'Other Document',
};

function DocumentViewer({ documents }: { documents: Document[] }) {
  const [selected, setSelected] = useState<Document | null>(documents[0] ?? null);

  const isPdf = selected?.url.endsWith('.pdf');

  return (
    <div className="h-full flex flex-col">
      {/* Doc list */}
      <div className="flex gap-2 flex-wrap p-4 border-b border-slate-200 bg-slate-50">
        {documents.map((doc) => (
          <button
            key={doc.id}
            onClick={() => setSelected(doc)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              selected?.id === doc.id
                ? 'bg-primary-600 text-white'
                : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50'
            }`}
          >
            {doc.url.endsWith('.pdf') ? (
              <FileText className="h-3.5 w-3.5" />
            ) : (
              <Image className="h-3.5 w-3.5" />
            )}
            {DOC_LABELS[doc.type] ?? doc.type}
          </button>
        ))}
      </div>

      {/* Viewer */}
      <div className="flex-1 relative bg-slate-100 flex items-center justify-center overflow-hidden">
        {!selected ? (
          <p className="text-slate-400 text-sm">No documents uploaded</p>
        ) : isPdf ? (
          <iframe
            src={selected.url}
            className="w-full h-full"
            title={selected.fileName}
          />
        ) : (
          <div className="relative max-w-full max-h-full p-4">
            <img
              src={selected.url}
              alt={selected.fileName}
              className="max-w-full max-h-full object-contain rounded shadow"
            />
            <a
              href={selected.url}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute top-6 right-6 bg-black/50 hover:bg-black/70 text-white p-2 rounded-lg transition-colors"
              title="Open in new tab"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        )}
      </div>

      {selected && (
        <div className="px-4 py-2 border-t border-slate-200 bg-slate-50">
          <p className="text-xs text-slate-400">
            {DOC_LABELS[selected.type]} · {selected.fileName}
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Approval Form ────────────────────────────────────────────────────────────

// Canonical KYC document keys — must match the Vendor entity `documents` keys
// and the vendor dashboard re-upload UI so per-document verdicts line up.
const KYC_DOC_KEYS: { key: string; label: string }[] = [
  { key: 'panCard', label: 'PAN / VAT certificate' },
  { key: 'citizenshipFront', label: 'Citizenship — front' },
  { key: 'citizenshipBack', label: 'Citizenship — back' },
  { key: 'businessRegistration', label: 'Business registration' },
];

function ApprovalForm({ vendorId, onSuccess }: { vendorId: string; onSuccess: () => void }) {
  const [action, setAction] = useState<'approve' | 'reject'>('approve');
  // Per-document rejection notes the admin fills in. A non-empty note (or just
  // ticking) flags that document so the vendor knows to re-upload only it.
  const [docFlags, setDocFlags] = useState<Record<string, { rejected: boolean; note: string }>>({});

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<VerificationFormData>({
    resolver: zodResolver(verificationSchema),
    defaultValues: { action: 'approve' },
  });

  const approveMutation = useMutation({
    mutationFn: (notes?: string) => adminApi.approveVendor(vendorId, notes),
    onSuccess,
  });

  const rejectMutation = useMutation({
    mutationFn: (data: {
      reason: string;
      notes?: string;
      documentReviews?: Record<string, { status: 'rejected'; note?: string }>;
    }) => adminApi.rejectVendor(vendorId, data.reason, data.notes, data.documentReviews),
    onSuccess,
  });

  const toggleDoc = (key: string) =>
    setDocFlags((f) => ({ ...f, [key]: { rejected: !f[key]?.rejected, note: f[key]?.note ?? '' } }));
  const setDocNote = (key: string, note: string) =>
    setDocFlags((f) => ({ ...f, [key]: { rejected: f[key]?.rejected ?? true, note } }));

  const onSubmit = (data: VerificationFormData) => {
    if (data.action === 'approve') {
      approveMutation.mutate(data.internalNotes);
    } else {
      const documentReviews: Record<string, { status: 'rejected'; note?: string }> = {};
      for (const { key } of KYC_DOC_KEYS) {
        if (docFlags[key]?.rejected) {
          documentReviews[key] = { status: 'rejected', note: docFlags[key]?.note?.trim() || undefined };
        }
      }
      rejectMutation.mutate({
        reason: data.rejectionReason,
        notes: data.internalNotes,
        documentReviews: Object.keys(documentReviews).length ? documentReviews : undefined,
      });
    }
  };

  const isLoading = approveMutation.isPending || rejectMutation.isPending || isSubmitting;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      {/* Action selector */}
      <div>
        <p className="text-sm font-semibold text-slate-700 mb-2">Verification Decision</p>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setAction('approve')}
            className={`flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-medium text-sm transition-all ${
              action === 'approve'
                ? 'border-green-500 bg-green-50 text-green-700'
                : 'border-slate-200 text-slate-500 hover:border-slate-300'
            }`}
          >
            <CheckCircle className={`h-5 w-5 ${action === 'approve' ? 'text-green-500' : 'text-slate-400'}`} />
            Approve
          </button>
          <button
            type="button"
            onClick={() => setAction('reject')}
            className={`flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-medium text-sm transition-all ${
              action === 'reject'
                ? 'border-red-500 bg-red-50 text-red-700'
                : 'border-slate-200 text-slate-500 hover:border-slate-300'
            }`}
          >
            <XCircle className={`h-5 w-5 ${action === 'reject' ? 'text-red-500' : 'text-slate-400'}`} />
            Reject
          </button>
        </div>
      </div>

      <input type="hidden" {...register('action')} value={action} />

      {/* Rejection reason */}
      {action === 'reject' && (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Rejection Reason <span className="text-red-500">*</span>
          </label>
          <textarea
            {...register('rejectionReason' as never)}
            rows={3}
            placeholder="Explain why this application is being rejected. This message will be sent to the vendor."
            className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              (errors as Record<string, { message?: string }>).rejectionReason ? 'border-red-400' : 'border-slate-300'
            }`}
          />
          {(errors as Record<string, { message?: string }>).rejectionReason && (
            <p className="mt-1 text-xs text-red-500">
              {(errors as Record<string, { message?: string }>).rejectionReason?.message}
            </p>
          )}

          {/* Per-document verdicts — tick the documents that are invalid so the
              vendor can re-upload ONLY those instead of redoing everything. */}
          <div className="mt-4">
            <p className="block text-sm font-medium text-slate-700 mb-2">
              Flag specific documents <span className="text-xs text-slate-400 font-normal">(optional)</span>
            </p>
            <div className="space-y-2">
              {KYC_DOC_KEYS.map(({ key, label }) => {
                const flagged = docFlags[key]?.rejected ?? false;
                return (
                  <div key={key} className={`rounded-lg border p-2.5 ${flagged ? 'border-red-300 bg-red-50/60' : 'border-slate-200'}`}>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={flagged} onChange={() => toggleDoc(key)} className="accent-red-600" />
                      <span className="text-sm font-medium text-slate-800">{label}</span>
                    </label>
                    {flagged && (
                      <input
                        value={docFlags[key]?.note ?? ''}
                        onChange={(e) => setDocNote(key, e.target.value)}
                        placeholder="What's wrong with this document? (shown to vendor)"
                        className="mt-2 w-full border border-slate-300 rounded-md px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-red-400"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Internal notes */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Internal Notes
          <span className="text-xs text-slate-400 font-normal ml-1">(not visible to vendor)</span>
        </label>
        <textarea
          {...register('internalNotes' as never)}
          rows={2}
          placeholder="Any notes for the admin team..."
          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={isLoading}
        className={`w-full py-3 rounded-xl font-semibold text-sm transition-colors flex items-center justify-center gap-2 ${
          action === 'approve'
            ? 'bg-green-600 hover:bg-green-700 text-white'
            : 'bg-red-600 hover:bg-red-700 text-white'
        } disabled:opacity-50`}
      >
        {isLoading ? (
          <span className="animate-pulse">Processing...</span>
        ) : action === 'approve' ? (
          <>
            <CheckCircle className="h-4 w-4" /> Approve Vendor
          </>
        ) : (
          <>
            <XCircle className="h-4 w-4" /> Reject Application
          </>
        )}
      </button>
    </form>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function VendorVerification() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['admin-vendor', id],
    queryFn: () => adminApi.getVendor(id!),
    select: (res) => res.data.data,
    enabled: !!id,
  });

  const vendor = data?.vendor;
  const documents: Document[] = data?.documents ?? [];

  const handleSuccess = () => {
    navigate('/vendors');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-primary-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Back */}
      <button
        onClick={() => navigate('/vendors')}
        className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 transition-colors"
      >
        <ChevronLeft className="h-4 w-4" />
        Back to Vendors
      </button>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Vendor Verification</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {vendor?.storeName} — {vendor?.ownerName}
          </p>
        </div>
        <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-3 py-1 rounded-full">
          {vendor?.kycStatus?.replace('_', ' ') || 'Under Review'}
        </span>
      </div>

      {/* Split view */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6" style={{ minHeight: '600px' }}>
        {/* Document viewer */}
        <div className="xl:col-span-2 bg-white rounded-xl border border-slate-200 overflow-hidden" style={{ minHeight: '500px' }}>
          <DocumentViewer documents={documents} />
        </div>

        {/* Approval form */}
        <div className="xl:col-span-1 space-y-4">
          {/* Vendor summary */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-3">Vendor Information</h3>
            <dl className="space-y-2 text-sm">
              {[
                { label: 'Store Name', value: vendor?.storeName },
                { label: 'Owner', value: vendor?.ownerName },
                { label: 'Email', value: vendor?.email },
                { label: 'Phone', value: vendor?.phone },
                { label: 'PAN Number', value: vendor?.panNumber },
                { label: 'Business Type', value: vendor?.businessType },
                { label: 'District', value: vendor?.district },
              ].map(({ label, value }) =>
                value ? (
                  <div key={label} className="flex justify-between gap-2">
                    <dt className="text-slate-500 flex-shrink-0">{label}</dt>
                    <dd className="text-slate-900 font-medium text-right truncate">{value}</dd>
                  </div>
                ) : null
              )}
            </dl>
          </div>

          {/* Approval form */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-4">Verification Decision</h3>
            {id && <ApprovalForm vendorId={id} onSuccess={handleSuccess} />}
          </div>
        </div>
      </div>
    </div>
  );
}
